// src/components/Calendar/CalendarModal.jsx
import React, { useState, useContext } from 'react';
import { Box, Typography, Grid, Paper, IconButton, Dialog, DialogContent, List, ListItemText, useTheme, DialogTitle, Divider, ListItemButton } from '@mui/material';
import { ChevronLeft, ChevronRight, Event as EventIcon, Close as CloseIcon } from '@mui/icons-material';
import { format, addMonths, subMonths, startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, isSameMonth, isToday, isSameDay } from 'date-fns';
import ModalContext from '../../shared/ModalContext/ModalContext';

export const CalendarModal = ({ open, onClose, props }) => {
  const events = props?.events || [];
  const theme = useTheme();
  const { openModal } = useContext(ModalContext);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());

  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(monthStart);
  const startDate = startOfWeek(monthStart);
  const endDate = endOfWeek(monthEnd);

  const days = eachDayOfInterval({ start: startDate, end: endDate });
  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const dailyEvents = events.filter(event => isSameDay(new Date(event.date), selectedDate));

  const handleEventClick = (event) => {
    openModal('MEETING_DETAILS', { event });
  };

  return (
    <Dialog open={open} onClose={onClose} PaperProps={{ sx: { p: 0, borderRadius: 4, bgcolor: 'background.paper', color: 'text.primary', maxWidth: 420 } }}>
      <DialogTitle sx={{ p: 2, pb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
        <Typography variant="h6" component="span" sx={{ fontWeight: 600 }}>{format(currentMonth, 'MMMM yyyy')}</Typography>
        <Box>
            <IconButton size="small" onClick={prevMonth}><ChevronLeft /></IconButton>
            <IconButton size="small" onClick={nextMonth} sx={{ mr: 1 }}><ChevronRight /></IconButton>
            <IconButton size="small" onClick={onClose}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Grid container spacing={1} sx={{ textAlign: 'center', mb: 2 }}>
            {dayNames.map(day => (
                <Grid item xs={12 / 7} key={day}><Typography variant="caption" sx={{ color: 'text.secondary' }}>{day}</Typography></Grid>
            ))}
            {days.map((day, i) => {
              const hasEvent = events.some(event => isSameDay(new Date(event.date), day));
              return (
                <Grid item xs={12 / 7} key={i}>
                    <Box
                        onClick={() => setSelectedDate(day)}
                        sx={{
                            bgcolor: isSameDay(day, selectedDate) ? 'primary.main' : (isToday(day) ? 'action.hover' : 'transparent'),
                            color: isSameDay(day, selectedDate) ? 'primary.contrastText' : (isSameMonth(day, monthStart) ? 'text.primary' : 'text.disabled'),
                            borderRadius: '50%',
                            width: 36,
                            height: 36,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            position: 'relative',
                            cursor: 'pointer',
                            fontWeight: isToday(day) || isSameDay(day, selectedDate) ? 'bold' : 'normal',
                            transition: 'background-color 0.2s, color 0.2s',
                            '&:hover': { bgcolor: !isSameDay(day, selectedDate) && 'action.selected' }
                        }}
                    >
                        {format(day, 'd')}
                        {hasEvent && (
                             <Box sx={{ height: 5, width: 5, bgcolor: isSameDay(day, selectedDate) ? 'primary.contrastText' : 'primary.main', borderRadius: '50%', position: 'absolute', bottom: 4 }} />
                        )}
                    </Box>
                </Grid>
            );
            })}
        </Grid>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ minHeight: 150 }}>
            <Typography variant="subtitle1" sx={{fontWeight: 500, mb: 2}}>{`Agenda for ${format(selectedDate, 'MMMM d')}`}</Typography>
            {dailyEvents.length > 0 ? (
                <List disablePadding>
                    {dailyEvents.map(event => (
                        <ListItemButton key={event.id} onClick={() => handleEventClick(event)} sx={{ bgcolor: 'action.hover', borderRadius: 2, mb: 1, p: 1.5 }}>
                            <ListItemText
                                primary={<Typography variant="body2" sx={{ fontWeight: 500 }}>{`Interview: ${event.candidate}`}</Typography>}
                                secondary={`${event.title} at ${event.time}`}
                            />
                        </ListItemButton>
                    ))}
                </List>
            ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', pt: 3 }}>
                    <EventIcon sx={{ color: 'text.disabled', fontSize: 32 }} />
                    <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', mt: 1}}>No events scheduled for this day.</Typography>
                </Box>
            )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};